import React, { useEffect, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { Worker } from "@react-pdf-viewer/core";
import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import { CloseIcon, FullScreenExitIcon, FullScreenIcon, LinkIcon, PDFPrev } from "../../assets/icons/icons";
import AudioWaveform from "./AudioWaveform";

const HotspotPopUp = ({ onClose = () => {}, showHotspotModal, currentHotspot, toggleHotspotModal }) => {
  const [contentLoaded, setContentLoaded] = useState(false);
  const [bigWindow, setBigWindow] = useState(false);

  // Get the first action from currentHotspot
  const action = currentHotspot?.action?.[0];
  const actionInventory = action?.inventory;
  const contentType = actionInventory?.type;
  const contentSource = actionInventory?.path;
  const title = action?.title;
  const description = action?.description;

  const transform = (slot) => ({
    ...slot,
    Download: () => <></>,
    DownloadMenuItem: () => <></>,
    SwitchTheme: () => <></>,
    SwitchThemeMenuItem: () => <></>,
    Open: () => <></>,
    Print: () => <></>,
    ShowProperties: () => <></>,
    ShowPropertiesMenuItem: () => <></>,
  });

  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar } = toolbarPluginInstance;
  const renderToolbar = (Toolbar) => <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar,
    sidebarTabs: (defaultTabs) => [defaultTabs[0]],
  });

  // Handle content loading
  useEffect(() => {
    if (contentSource) {
      setContentLoaded(false);
      // Simulate content loading
      const timer = setTimeout(() => {
        setContentLoaded(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [contentSource]);

  // Debug logging for transition states
  useEffect(() => {
    console.log('HotspotPopUp showHotspotModal changed:', showHotspotModal);
  }, [showHotspotModal]);

  // Don't render if no currentHotspot or no content at all
  if (!currentHotspot) {
    return null;
  }

  // Handle content loading events
  const handleContentLoad = () => {
    setContentLoaded(true);
  };

  const handleContentError = () => {
    console.warn("Failed to load hotspot content:", contentSource);
    setContentLoaded(true); // Still set as loaded to hide spinner
  };

  return (
    <>
      <CSSTransition 
        in={showHotspotModal} 
        timeout={500} 
        classNames="fade" 
        unmountOnExit
        // onEnter={() => console.log('CSSTransition onEnter')}
        // onEntering={() => console.log('CSSTransition onEntering')}
        // onEntered={() => console.log('CSSTransition onEntered')}
        // onExit={() => console.log('CSSTransition onExit')}
        // onExiting={() => console.log('CSSTransition onExiting')}
        // onExited={() => console.log('CSSTransition onExited')}
      >
        <div className="hotspotHighlightContainer">
          <span className="bg-overlay" onClick={onClose}></span>
          <div className="hotspotHighlight">
            {contentSource && (
              <div className={`content ${(!title && !description) || bigWindow ? "withDetails" : ""}`}>
                {!contentLoaded ? (
                  <div className="spinner center">
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                    <div className="spinner-blade"></div>
                  </div>
                ) : (
                  <>
                    {contentType === "IMAGE" ? (
                      <img
                        src={contentSource}
                        referrerPolicy="no-referrer"
                        alt={title}
                        onLoad={handleContentLoad}
                        onError={handleContentError}
                      />
                    ) : contentType === "VIDEO" ? (
                      <video
                        loop
                        src={contentSource}
                        autoPlay
                        onLoadedData={handleContentLoad}
                        onError={handleContentError}
                      />
                    ) : contentType === "PDF" ? (
                      <div className="pdfPreview">
                        <PDFPrev />
                      </div>
                    ) : contentType === "AUDIO" ? (
                      <AudioWaveform audioUrl="https://cdn.simulanis.io/creator/uno/production/resources/bff38c6d-1f0e-4204-b841-6d3351390891/AUDIO/57d6dbd9-294a-4a11-990c-6641e92e7acb.mp3" />
                    ) : null}
                  </>
                )}
                {contentLoaded && contentType !== "AUDIO" && (
                  <span
                    className="closeIcon"
                    onClick={() => {
                      setBigWindow((Prev) => !Prev);
                      toggleHotspotModal(false);
                    }}
                  >
                    <FullScreenIcon />
                  </span>
                )}
              </div>
            )}
            {(title || description) && (
              <div className="details">
                {title && <h6>{title}</h6>}
                {description && <p>{description}</p>}
              </div>
            )}
            <span
              className="closeIcon"
              onClick={() => {
                setBigWindow(false);
                onClose();
              }}
            >
              <CloseIcon />
            </span>
          </div>
        </div>
      </CSSTransition>

      <CSSTransition in={bigWindow} timeout={300} classNames="fade" unmountOnExit>
        <div className="hotspotHighlightContainer">
          <span className="bg-overlay" onClick={() => setBigWindow(false)}></span>
          <div className="hotspotHighlight bigWindow">
            <div
              className={`content ${contentType === "PDF" ? "pdfViewer" : ""} ${
                (!title && !description) || bigWindow ? "withDetails" : ""
              }`}
            >
              {!contentLoaded ? (
                <div className="spinner center">
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                  <div className="spinner-blade"></div>
                </div>
              ) : (
                <>
                  {contentType === "IMAGE" ? (
                    <img
                      src={contentSource}
                      referrerPolicy="no-referrer"
                      alt={title}
                      onLoad={handleContentLoad}
                      onError={handleContentError}
                    />
                  ) : contentType === "VIDEO" ? (
                    <video
                      loop
                      src={contentSource}
                      autoPlay
                      controls
                      onLoadedData={handleContentLoad}
                      onError={handleContentError}
                    />
                  ) : contentType === "PDF" ? (
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                      <Viewer fileUrl={contentSource} plugins={[defaultLayoutPluginInstance]} />
                    </Worker>
                  ) : null}
                </>
              )}
              {contentLoaded && (
                <span
                  className="closeIcon"
                  onClick={() => {
                    setBigWindow((Prev) => !Prev);
                    toggleHotspotModal(true);
                  }}
                >
                  <FullScreenExitIcon />
                </span>
              )}
            </div>

            <span
              className="closeIcon"
              onClick={() => {
                setBigWindow(false);
                onClose();
              }}
            >
              <CloseIcon />
            </span>
          </div>
        </div>
      </CSSTransition>
    </>
  );
};

export default HotspotPopUp;